import { useState } from "react";
import { Link } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaClock,
  FaPhone,
  FaWhatsapp,
  FaEnvelope,
  FaUserCircle,
} from "react-icons/fa";
import { MdOutlineQuestionAnswer, MdOutlineFeedback } from "react-icons/md";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <span className="text-blue-900 mt-0.5 text-lg shrink-0">{icon}</span>
    <p className="text-gray-700">
      <span className="font-semibold">{label}:</span> {value}
    </p>
  </div>
);

const SectionCard = ({ title, icon, children, id }) => (
  <div id={id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
    <h2 className="text-xl font-bold text-blue-950 flex items-center gap-2">
      <span className="text-blue-700 text-2xl">{icon}</span>
      {title}
    </h2>
    {children}
  </div>
);

const SECTIONS = [
  {
    id: "sic-presencial",
    title: "SIC Presencial",
    keywords: "sic presencial endereço horário telefone atendimento",
  },
  {
    id: "esic-formulario",
    title: "e-SIC — Formulário Eletrônico",
    keywords: "esic formulario eletrônico solicitação protocolo cpf email pedido anexo",
  },
  {
    id: "ouvidoria",
    title: "Ouvidoria",
    keywords: "ouvidoria denúncia reclamação sugestão elogio sigilo",
  },
  {
    id: "canais",
    title: "Canais de Atendimento",
    keywords: "canais whatsapp telefone email presencial faq contato",
  },
];

const CommunicationChannels = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const normalized = searchTerm.toLowerCase();

  const isVisible = (section) => {
    if (!searchTerm) return true;
    return (
      section.title.toLowerCase().includes(normalized) ||
      section.keywords.includes(normalized)
    );
  };

  const noResults = SECTIONS.every((s) => !isVisible(s));

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-4xl mx-auto">

      {/* Search */}
      <div className="flex flex-col items-center gap-3 py-2">
        <p className="text-sm font-semibold uppercase tracking-widest text-blue-900">
          Encontre o que você precisa
        </p>
        <div className="relative w-full max-w-2xl">
          <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Pesquisar canais e serviços..."
            className="w-full border border-gray-300 rounded-xl pl-12 pr-4 py-3 text-base text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
          />
        </div>
      </div>

      {/* Intro */}
      {!searchTerm && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-5">
          <p className="text-blue-900 text-base leading-relaxed">
            O <strong>Serviço de Informação ao Cidadão (SIC)</strong> garante o direito de acesso
            à informação pública previsto na <strong>Lei nº 12.527/2011 (Lei de Acesso à Informação)</strong>.
            Qualquer cidadão pode solicitar informações à Prefeitura de Serrinha gratuitamente.
          </p>
        </div>
      )}

      {noResults && (
        <p className="text-gray-500 text-center py-8">
          Nenhum resultado encontrado para "{searchTerm}".
        </p>
      )}

      {/* 1 - SIC Presencial */}
      {isVisible(SECTIONS[0]) && (
        <SectionCard id="sic-presencial" title="SIC Presencial" icon={<FaMapMarkerAlt />}>
          <p className="text-gray-600 text-sm">
            Atendimento presencial para solicitações, acompanhamento e retirada de documentos.
          </p>
          <div className="space-y-3 mt-2">
            <InfoRow
              icon={<FaMapMarkerAlt />}
              label="Endereço"
              value="Rua Manoel Joaquim de Souza, 150, Centro — Serrinha/RN, CEP 59.258-000"
            />
            <InfoRow
              icon={<FaClock />}
              label="Horário"
              value="Segunda a Sexta, das 08h às 13h"
            />
            <InfoRow
              icon={<FaPhone />}
              label="Telefone"
              value="(84) 99999-9999"
            />
          </div>
        </SectionCard>
      )}

      {/* 2 - e-SIC */}
      {isVisible(SECTIONS[1]) && (
        <SectionCard id="esic-formulario" title="e-SIC — Formulário Eletrônico" icon={<MdOutlineQuestionAnswer />}>
          <p className="text-gray-600 text-sm">
            Envie sua solicitação de acesso à informação de forma online, sem sair de casa.
            Você receberá um número de protocolo automático para acompanhamento.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700 mt-1">
            {[
              "Nome completo",
              "CPF (opcional)",
              "E-mail para retorno",
              "Tipo de solicitação",
              "Descrição do pedido",
              "Anexos (opcional)",
            ].map((field) => (
              <div key={field} className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-700 shrink-0" />
                {field}
              </div>
            ))}
          </div>
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            <strong>Em breve:</strong> o formulário eletrônico estará disponível nesta página.
            Enquanto isso, utilize o atendimento presencial ou os canais abaixo.
          </div>
          {/* Uncomment and replace href when Google Form is ready:
          <a
            href="LINK_DO_GOOGLE_FORMS"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-blue-900 text-white font-semibold py-2.5 px-5 rounded-lg hover:bg-blue-700 transition mt-2"
          >
            Acessar formulário <FaExternalLinkAlt className="text-sm" />
          </a>
          */}
        </SectionCard>
      )}

      {/* 3 - Ouvidoria */}
      {isVisible(SECTIONS[2]) && (
        <SectionCard id="ouvidoria" title="Ouvidoria" icon={<MdOutlineFeedback />}>
          <p className="text-gray-600 text-sm">
            A Ouvidoria é um canal direto entre o cidadão e a administração municipal.
            Use-a para registrar:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
            {[
              { label: "Denúncias", color: "bg-red-50 border-red-200 text-red-800" },
              { label: "Reclamações", color: "bg-orange-50 border-orange-200 text-orange-800" },
              { label: "Sugestões", color: "bg-green-50 border-green-200 text-green-800" },
              { label: "Elogios", color: "bg-blue-50 border-blue-200 text-blue-800" },
            ].map(({ label, color }) => (
              <div
                key={label}
                className={`border rounded-lg py-3 px-4 text-center text-sm font-semibold ${color}`}
              >
                {label}
              </div>
            ))}
          </div>
          <div className="mt-3 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
            As manifestações são tratadas com sigilo e respondidas em até <strong>30 dias úteis</strong>,
            conforme a Lei de Acesso à Informação.
          </div>
          <Link
            to="/transparencia/ouvidoria"
            className="inline-flex items-center gap-2 bg-blue-900 text-white font-semibold py-2.5 px-5 rounded-lg hover:bg-blue-700 transition mt-2"
          >
            Acessar Ouvidoria
          </Link>
        </SectionCard>
      )}

      {/* 4 - Canais de Atendimento */}
      {isVisible(SECTIONS[3]) && (
        <SectionCard id="canais" title="Canais de Atendimento" icon={<FaUserCircle />}>
          <p className="text-gray-600 text-sm">
            Escolha o canal mais conveniente para entrar em contato com a Prefeitura de Serrinha.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <a
              href="https://wa.me/5584999999999"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-green-50 hover:border-green-300 transition group"
            >
              <FaWhatsapp className="text-2xl text-green-600" />
              <div>
                <p className="font-semibold text-gray-800 group-hover:text-green-800">WhatsApp</p>
                <p className="text-sm text-gray-500">(84) 99999-9999</p>
              </div>
            </a>
            <a
              href="tel:+558499999999"
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition group"
            >
              <FaPhone className="text-2xl text-blue-700" />
              <div>
                <p className="font-semibold text-gray-800 group-hover:text-blue-800">Telefone</p>
                <p className="text-sm text-gray-500">(84) 99999-9999</p>
              </div>
            </a>
            <a
              href="mailto:contato@serrinha.rn.gov.br"
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition group"
            >
              <FaEnvelope className="text-2xl text-blue-700" />
              <div>
                <p className="font-semibold text-gray-800 group-hover:text-blue-800">E-mail</p>
                <p className="text-sm text-gray-500">contato@serrinha.rn.gov.br</p>
              </div>
            </a>
            <div className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <FaMapMarkerAlt className="text-2xl text-blue-950" />
              <div>
                <p className="font-semibold text-gray-800">Atendimento Presencial</p>
                <p className="text-sm text-gray-500">Seg–Sex, 08h às 13h</p>
              </div>
            </div>
          </div>
          <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-800">
            <strong>Perguntas Frequentes (FAQ):</strong> Em breve disponibilizaremos um FAQ
            com as dúvidas mais comuns sobre acesso à informação e serviços da Prefeitura.
          </div>
        </SectionCard>
      )}

    </div>
  );
};

export default CommunicationChannels;
